import { useAuth } from '@/features/auth/hooks/useAuth';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Concern, CONCERN_VALIDATION } from '@promo/shared';
import { useConcerns } from '../hooks/useConcerns';
import { ConcernForm } from './ConcernForm';
import { UserInfo } from '@/components/common/UserInfo';
import { useUser } from '@/hooks/useUser';
import { getUserInitials } from '@promo/shared';
import { useState } from 'react';

interface ConcernsSectionProps {
  packetId: string;
  readOnly?: boolean;
  isEditor?: boolean;
  isSeniorLeader?: boolean; // senior leader assigned to this packet
}

interface ConcernItemProps {
  concern: Concern;
  currentUserId: string;
  isEditable: boolean;
  canModifyAny: boolean;
  updateConcern: (id: string, data: { content?: string; authorEmail?: string }) => void;
  isUpdating: boolean;
  deleteConcern: (id: string) => void;
  isDeleting: boolean;
}

function ConcernItem({
  concern,
  currentUserId,
  isEditable,
  canModifyAny,
  updateConcern,
  isUpdating,
  deleteConcern,
  isDeleting,
}: ConcernItemProps) {
  const { data: user } = useUser(concern.authorId);
  const initials = user ? getUserInitials(user) : 'AU';
  const name = user?.name ?? '';
  const isOwner = concern.authorId === currentUserId;
  const canModify = isEditable && (isOwner || canModifyAny);

  const showEditableTitle = canModify && !!user;

  return (
    <div className="not-first:py-6 pb-10">
      <div className="mb-4">
        <ConcernForm
          initialContent={concern.content}
          initialAuthorEmail={user?.email}
          onSubmit={canModify ? (content, authorEmail) => updateConcern(concern.id, { content, authorEmail }) : () => {}}
          onCancel={() => {}}
          onDelete={canModify ? () => deleteConcern(concern.id) : undefined}
          isSubmitting={isUpdating || isDeleting}
          readOnly={!canModify}
        />
      </div>
      {/* Grid for right-aligned user info like endorsements */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
        <div className="hidden md:block md:col-span-3" />
        <div className="col-span-4 md:col-span-1 flex justify-start">
          <UserInfo
            initials={initials}
            name={name}
            userId={user?.id}
            currentTitle={user?.title}
            email={user?.email}
            editableEmail={canModifyAny}
            editableTitle={showEditableTitle}
            onEmailUpdate={(email) => {
              updateConcern(concern.id, { authorEmail: email });
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function ConcernsSection({ packetId, readOnly, isEditor, isSeniorLeader }: ConcernsSectionProps) {
  const { session } = useAuth();
  const currentUserId = session.user?.id ?? '';
  const isEditable = !readOnly;
  const canModifyAny = Boolean(isEditor || isSeniorLeader);

  const {
    concerns,
    isLoadingConcerns,
    concernsError,
    createConcern,
    isCreatingConcern,
    updateConcern,
    isUpdatingConcern,
    deleteConcern,
    isDeletingConcern,
  } = useConcerns(packetId);

  // New form key state to reset after submit/cancel and author email state
  const [newFormKey, setNewFormKey] = useState(0);

  const userConcern = concerns.find((c) => c.authorId === currentUserId);

  const { data: currentUser } = useUser(currentUserId);
  const currentInitials = currentUser ? getUserInitials(currentUser) : 'ME';
  const placeholderInitials = 'AU';
  const defaultName = currentUser?.name ?? '';
  const currentName = canModifyAny ? 'Author' : defaultName;

  if (isLoadingConcerns) {
    return (
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <h2 className="text-xl font-semibold">Opportunities</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Spinner className="h-8 w-8" />
          <span className="ml-2">Loading...</span>
        </div>
      </div>
    );
  }

  if (concernsError) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">Opportunities</h2>
        <div className="bg-red-50 text-red-800 p-4 rounded-md">
          <h3 className="text-lg font-semibold">Error loading concerns</h3>
          <p>{concernsError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-4">
      <div className="flex items-center space-x-2 mb-6">
        <h2 className="text-xl font-semibold">Opportunities</h2>
        <Badge>
          {concerns.length} of {CONCERN_VALIDATION.MAX_CONCERNS}
        </Badge>
      </div>
      <div>
        <p className="mb-6">
            Add any constructive concerns or growth opportunities that decision-makers should consider.
        </p>
        {canModifyAny && (
        <p className="mb-10">
            You may enter opportunities on behalf of others or invite them to enter their own. Enter their email
            and they will receive an email to enter their own.
        </p>
        )}
      </div>

      {/* Empty state skeleton */}
      {concerns.length === 0 && (
        <div className="space-y-4">
          <Skeleton className="h-4 w-3/4" />
          <div className="flex items-center space-x-3 pt-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        </div>
      )}

      {/* Existing concerns */}
      {concerns.map((c) => (
        <ConcernItem
          key={c.id}
          concern={c}
          currentUserId={currentUserId}
          isEditable={isEditable}
          canModifyAny={canModifyAny}
          updateConcern={updateConcern}
          isUpdating={isUpdatingConcern}
          deleteConcern={deleteConcern}
          isDeleting={isDeletingConcern}
        />
      ))}

      {/* New concern form */}
      {isEditable && (canModifyAny || !userConcern) && concerns.length < CONCERN_VALIDATION.MAX_CONCERNS && (
        <div className="py-6">
          <ConcernForm
            key={newFormKey}
            initialAuthorEmail={undefined}
            readOnly={canModifyAny}
            onSubmit={(content, authorEmail) =>
              createConcern({ packetId, content, authorEmail }, {
                onSuccess: () => { setNewFormKey((k)=>k+1); },
              })
            }
            onCancel={() => { setNewFormKey((k)=>k+1); }}
            isSubmitting={isCreatingConcern}
          />

          {/* User info grid aligned like other items */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 pt-4">
            <div className="hidden md:block md:col-span-3" />
            <div className="col-span-4 md:col-span-1 flex justify-start">
              <UserInfo
                key={newFormKey}
                initials={canModifyAny ? placeholderInitials : currentInitials}
                name={currentName}
                userId={canModifyAny ? undefined : currentUser?.id}
                email={undefined}
                currentTitle={canModifyAny ? undefined : currentUser?.title}
                editableTitle={Boolean(isEditable && currentUser)}
                editableEmail={canModifyAny}
                onEmailUpdate={(email)=> {
                  if (canModifyAny) {
                    // Immediately create blank concern
                    createConcern({ packetId, content: '', authorEmail: email }, {
                      onSuccess: () => {
                        setNewFormKey((k)=>k+1);
                      }
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
